import { IWebsiteProduct } from '@src/components/product-card/product-card.types'

type ObjType =
  | Record<string, unknown>
  | string[]
  | number[]
  | boolean[]
  | Set<unknown>
  | Map<unknown, unknown>
  | null
  | undefined

/**
 * Checks if the given value (object, array, set, map, etc.) is empty.
 * @param {object | null | undefined | any[]} obj - The value to check.
 * @returns {boolean} - True if empty; otherwise, false.
 */
export const isEmpty = (obj: ObjType): boolean => {
  if (obj == null) return true
  if (obj instanceof Set || obj instanceof Map) return obj.size === 0
  if (Array.isArray(obj)) return obj.length === 0
  return Object.keys(obj).length === 0
}

export const pick = <T extends object, U extends keyof T>(obj: T, keys: U[]) => {
  const result: Partial<T> = {}
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

export const pickBy = <T extends Record<string, unknown>>(
  object: T,
  predicate: (value: T[keyof T], key: string) => boolean,
) => {
  const result: Partial<T> = {}

  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      if (predicate(object[key], key)) {
        result[key] = object[key]
      }
    }
  }

  return result
}

export const templateInterpolate = (str: string, settings: { interpolate: RegExp }) => {
  return (data: Record<string, string>) => {
    return str.replace(settings.interpolate, (_match, p1: string) => {
      const key = p1.trim()
      return data[key] ?? ''
    })
  }
}

export const kebabCase = (str: string) => {
  const regexForKebabCase = /(\d+|([A-Z][a-z]+)|[a-z]+|([A-Z]+)(?![a-z]))/g
  return (String(str).match(regexForKebabCase) ?? []).map(x => x.toLowerCase()).join('-')
}
export const keyBy = <T extends Record<string, unknown>, K extends keyof T | ((item: T) => string | number)>(
  array: T[],
  iteratee: K,
): Record<string, T> =>
  array.reduce<Record<string, T>>((result, item) => {
    const key =
      typeof iteratee === 'function'
        ? (iteratee as (item: T) => string | number)(item)
        : String(item[iteratee as keyof T])
    result[key] = item
    return result
  }, {})

export const takeWhile = <T>(array: T[], predicate: (element: T) => boolean): T[] => {
  const index = array.findIndex(element => !predicate(element))
  return index === -1 ? array : array.slice(0, index)
}

export const difference = <T>(arr1: T[], arr2: T[]) => arr1.filter(x => !arr2.includes(x))

export const partition = <T>(array: T[], predicate: (element: T) => boolean) => {
  return array.reduce(
    (result: [T[], T[]], element: T) => {
      predicate(element) ? result[0].push(element) : result[1].push(element)
      return result
    },
    [[], []],
  )
}

export const set = <T extends Record<string, unknown>>(obj: T, path: string | string[], value: unknown): void => {
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)

  pathArray?.reduce<Record<string, unknown>>((acc: Record<string, unknown>, key: string, i: number) => {
    if (acc[key] === undefined) acc[key] = {}
    if (i === pathArray.length - 1) acc[key] = value
    return acc[key] as Record<string, unknown>
  }, obj)
}

/**
 * Deeply compares two values (primitives, arrays, objects) for equality.
 * Recursively checks properties and elements in nested structures.
 * Returns true if both values are deeply equal, otherwise false.
 */
export function isEqual<T>(value1: T, value2: T): boolean {
  // Check if both values are strictly equal (handles primitive types)
  if (value1 === value2) {
    return true
  }

  // Check if both values are of type 'object' (handles objects and arrays)
  if (typeof value1 === 'object' && value1 !== null && typeof value2 === 'object' && value2 !== null) {
    // If they are arrays, compare lengths and elements
    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (value1.length !== value2.length) {
        return false
      }
      for (let i = 0; i < value1.length; i++) {
        if (!isEqual(value1[i], value2[i])) {
          return false
        }
      }
      return true
    }

    const keys1 = Object.keys(value1)
    const keys2 = Object.keys(value2)

    if (keys1.length !== keys2.length) {
      return false
    }

    for (const key of keys1) {
      if (!isEqual((value1 as Record<string, unknown>)[key], (value2 as Record<string, unknown>)[key])) {
        return false
      }
    }

    return true
  }

  return false
}

export const uniqueId = (() => {
  let counter = 0
  return (prefix = '') => `${prefix}${++counter}`
})()
export const transform = <T extends object>(
  obj: T,
  iteratee: (accumulator: Record<string, unknown> | unknown[], value: unknown, key: string, source: T) => void,
  accumulator: Record<string, unknown> | unknown[] = Array.isArray(obj) ? [] : {},
) => {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      iteratee(accumulator, obj[key], key, obj)
    }
  }

  return accumulator
}

export const get = <T extends Record<string, unknown>>(obj: T, path?: string, defValue?: unknown) => {
  if (!path) return undefined

  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
  const result = pathArray?.reduce<unknown>((prevObj, key) => {
    if (typeof prevObj === 'object' && prevObj !== null) {
      return (prevObj as Record<string, unknown>)[key as keyof typeof prevObj]
    }
    return undefined
  }, obj)
  return result === undefined ? defValue : result
}

export const find = <T extends { name: unknown }>(
  array: T[] | undefined,
  predicate: (item: T) => boolean,
): T | undefined => {
  return array?.find(predicate)
}

// Custom deep clone function
const customDeepClone = <T>(value: T, map = new WeakMap<object, unknown>()): T => {
  if (value === null || typeof value !== 'object') return value

  if (map.has(value)) return map.get(value) as T

  if (Array.isArray(value)) {
    const clone = [] as unknown[]
    map.set(value, clone)
    for (let i = 0; i < value.length; i++) {
      clone[i] = customDeepClone(value[i], map)
    }
    return clone as T
  }

  const clone = {} as Record<string, unknown>
  map.set(value, clone)

  for (const key of Object.keys(value as object)) {
    clone[key] = customDeepClone((value as Record<string, unknown>)[key], map)
  }

  return clone as T
}
// Fallback function using JSON serialization
const deepCloneWithJSON = <T>(object: T): T | null => {
  try {
    return JSON.parse(JSON.stringify(object)) as T
  } catch (error) {
    return null
  }
}

export const cloneDeep = <T>(object: T): T => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(object)
    } catch (error) {
      return deepCloneWithJSON(object) ?? customDeepClone(object)
    }
  } else {
    return deepCloneWithJSON(object) ?? customDeepClone(object)
  }
}

export const cloneDeepWith = <T>(object: T, customizer?: (value: T) => T | undefined) => {
  const customResult = customizer?.(object)
  if (customResult !== undefined) {
    return customResult
  }

  return cloneDeep(object)
}

export const noop = () => undefined

export const identity = <T>(a: T): T => a

/**
 * Transforms the values of an object by applying a transformation function to each value.
 * The keys of the object remain unchanged.
 *
 * @param object - The object to transform.
 * @param iteratee - The function to apply to each value.
 * @returns A new object with the same keys but transformed values.
 */
export function mapValues<T extends object, R>(
  object: T,
  iteratee: (value: T[keyof T], key: keyof T) => R,
): { [K in keyof T]: R } {
  const result = {} as { [K in keyof T]: R }

  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      result[key] = iteratee(object[key], key)
    }
  }

  return result
}

export const throttle = <T extends unknown[]>(func: (...args: T) => void, delay: number) => {
  let lastCall = 0

  return (...args: T) => {
    const now = new Date().getTime()
    if (now - lastCall >= delay) {
      lastCall = now
      func(...args)
    }
  }
}

export const debounce = <T extends unknown[]>(func: (...args: T) => void, delay: number, { leading = false } = {}) => {
  let timerId: NodeJS.Timeout | undefined

  return (...args: T) => {
    if (!timerId && leading) {
      func(...args)
    }
    clearTimeout(timerId)

    timerId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export const uniqBy = <T, K>(arr: T[], iteratee: ((item: T) => K) | keyof T) => {
  if (typeof iteratee === 'string') {
    const prop = iteratee as keyof T
    iteratee = (item: T) => item[prop] as K
  }

  return arr.filter(
    (x, i, self) => i === self.findIndex(y => (iteratee as (item: T) => K)(x) === (iteratee as (item: T) => K)(y)),
  )
}

export const round = (num: number, precision: number) => {
  const modifier = 10 ** precision
  return Math.round(num * modifier) / modifier
}

export const curry = <T extends (...args: unknown[]) => unknown>(func: T) => {
  // define the number of expected args
  const expectedArgs = func.length
  const curried = (...args: Parameters<T>) => {
    // if enough args has been passed return the
    // result of the function execution, otherwise
    // continue adding arguments to the list
    return args.length >= expectedArgs
      ? func(...args)
      : (...args2: unknown[]) => curried(...([...args, ...args2] as Parameters<T>))
  }
  return curried
}

export const clamp = (num: number, clamp: number, higher?: number) =>
  higher ? Math.min(Math.max(num, clamp), higher) : Math.min(num, clamp)

export const intersection = <T>(arr: T[], ...args: T[][]) => arr.filter(item => args.every(arr => arr.includes(item)))

export const intersectionWith = <T>(arr1: T[], arr2: T[], comparator = (a: T, b: T) => a === b) => {
  return arr1.filter(item1 => arr2.some(item2 => comparator(item1, item2)))
}
export const inRange = (num: number, rangeStart: number, rangeEnd = 0) =>
  (rangeStart < num && num < rangeEnd) || (rangeEnd < num && num < rangeStart)

export const omit = <T extends Record<string, unknown>>(obj: T, props: (keyof T)[]) => {
  const newObj = { ...obj }
  return Object.fromEntries(Object.entries(newObj).filter(([key]) => !props.includes(key as keyof T)))
}

export const getAddToCartTrackingDetails = (
  productCard: IWebsiteProduct,
  selectedColor: string | undefined,
  wishlistTrackingConfig: Record<string, unknown>,
) => {
  return {
    ...wishlistTrackingConfig,
    count_color_variant: selectedColor?.length,
    department: productCard.classification?.department,
    department_id: productCard.classification?.department_id,
    division: productCard.classification?.division,
    division_id: productCard.classification?.division_id,
    rating: productCard.reviews.averageRating,
    rating_count: productCard.reviews.reviewCount,
    subdepartment: productCard.classification?.subdepartment,
    subdepartment_id: productCard.classification?.subdepartment_id,
    title: productCard.title,
  }
}

/**
 * Type guard function that checks if a value is neither null nor undefined
 * @param value - The value to check
 * @returns true if the value is neither null nor undefined
 */
export function isNonNullish<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export const isTimerExpired = (endTime?: string): boolean => {
  if (!endTime) return true // No timer means expired
  const endTimeEpoch = Date.parse(endTime)
  // Handle invalid date strings
  if (Number.isNaN(endTimeEpoch)) {
    console.error(`Invalid endTime provided to isTimerExpired: "${endTime}"`)
    return true
  }
  const currentTime = Date.now()
  return currentTime >= endTimeEpoch
}

interface EmbeddedEntryNode {
  nodeType: string
  content?: ContentNode[]
  data?: {
    target?: {
      endTime?: string
    }
  }
}

interface ContentNode {
  content?: EmbeddedEntryNode[]
}

export interface IGenericContent {
  content?: {
    content?: ContentNode[]
  }
}

export function getEmbeddedEntryEndTime(genericContents?: IGenericContent[]): string | undefined {
  if (!genericContents) return undefined
  return (
    genericContents
      .flatMap(gc => gc.content?.content ?? [])
      .flatMap(node => node.content ?? [])
      .find(n => n.nodeType === 'embedded-entry-inline')?.data?.target?.endTime ?? undefined
  )
}
